import { API_ORIGIN, apiUrl } from "./apiOrigin";
// Payments.js — delivery payment plans across the fleet
// =============================================================================
// Reached from the left rail: Delivery ▶ Payments.
//
// A grid of branches. Each row shows how many patients have ever had a payment
// plan, how many have expired, and how many are still covered. The expired
// figure turns RED when some of those patients are still receiving deliveries
// — that is the number worth acting on, because it is unbilled work rather
// than housekeeping.
//
// Clicking a row expands it into the patient detail, worst first, with an
// "Email reminder" button per patient. The reminder is composed by the server
// and shown for approval before anything is sent.
//
// Backend:
//   GET  /api/headoffice/payments/summary
//   GET  /api/headoffice/payments/branch/:pharmacyId
//   POST /api/headoffice/payments/remind   { pharmacyId, patientId, send }
// =============================================================================

import React, { useCallback, useEffect, useMemo, useState } from "react";
const API_HOST = apiUrl("");
const C = {
  ink: "#1c2b4a",
  muted: "#5f5e5a",
  line: "#e6e4dd",
  good: "#1d6f42",
  goodBg: "#e4f2ed",
  bad: "#b3261e",
  badBg: "#fdecec",
  warn: "#854f0b",
  warnBg: "#faeeda",
  head: "#f1efe8"
};
const fmtDate = d => d ? new Date(d).toLocaleDateString("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric"
}) : "—";
const fmtMoney = n => typeof n === "number" ? `£${n.toFixed(2)}` : "—";
export default function Payments({
  userData,
  token
}) {
  const [branches, setBranches] = useState([]);
  const [windowDays, setWindowDays] = useState(60);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState({}); // pharmacyId -> detail
  const [detailBusy, setDetailBusy] = useState(null);
  const [filter, setFilter] = useState("all"); // all | expired | active
  const [preview, setPreview] = useState(null); // composed reminder awaiting approval
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState("");
  const api = useCallback(async (path, opts = {}) => {
    const res = await fetch(`${API_HOST}${path}`, {
      ...opts,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        ...(opts.headers || {})
      }
    });
    if (!res.ok) throw new Error(`${path} → ${res.status}`);
    return res.json();
  }, [token]);
  useEffect(() => {
    let dead = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const d = await api("/api/headoffice/payments/summary");
        if (dead) return;
        setBranches(Array.isArray(d?.branches) ? d.branches : []);
        if (d?.activeWindowDays) setWindowDays(d.activeWindowDays);
      } catch (e) {
        if (!dead) setError(String(e.message || e));
      } finally {
        if (!dead) setLoading(false);
      }
    })();
    return () => {
      dead = true;
    };
  }, [api]);
  const toggle = async b => {
    if (openId === b.pharmacyId) {
      setOpenId(null);
      return;
    }
    setOpenId(b.pharmacyId);
    setFilter("all");
    if (detail[b.pharmacyId]) return;
    setDetailBusy(b.pharmacyId);
    try {
      const d = await api(`/api/headoffice/payments/branch/${b.pharmacyId}`);
      setDetail(m => ({
        ...m,
        [b.pharmacyId]: d
      }));
    } catch (e) {
      setDetail(m => ({
        ...m,
        [b.pharmacyId]: {
          error: String(e.message || e),
          patients: []
        }
      }));
    } finally {
      setDetailBusy(null);
    }
  };
  const compose = async (branch, p) => {
    setToast("");
    try {
      const r = await api("/api/headoffice/payments/remind", {
        method: "POST",
        body: JSON.stringify({
          pharmacyId: branch.pharmacyId,
          patientId: p.patientId
        })
      });
      if (r.ok === false && r.reason === "no_email") {
        setToast(`${p.name} has no email address on record — no reminder can be sent.`);
        return;
      }
      setPreview({
        branch,
        patient: p,
        ...r
      });
    } catch (e) {
      setToast(`Could not compose reminder: ${e.message || e}`);
    }
  };
  const send = async () => {
    if (!preview) return;
    setSending(true);
    try {
      await api("/api/headoffice/payments/remind", {
        method: "POST",
        body: JSON.stringify({
          pharmacyId: preview.branch.pharmacyId,
          patientId: preview.patient.patientId,
          send: true
        })
      });
      setToast(`Reminder sent to ${preview.to}`);
      setPreview(null);
    } catch (e) {
      setToast(`Send failed: ${e.message || e}`);
    } finally {
      setSending(false);
    }
  };
  const totals = useMemo(() => branches.reduce((a, b) => ({
    ever: a.ever + (b.ever || 0),
    covered: a.covered + (b.covered || 0),
    expired: a.expired + (b.expired || 0),
    expiredActive: a.expiredActive + (b.expiredActive || 0)
  }), {
    ever: 0,
    covered: 0,
    expired: 0,
    expiredActive: 0
  }), [branches]);
  return <div style={{
    padding: 20,
    color: C.ink,
    fontFamily: "system-ui, sans-serif"
  }}>
      <h2 style={{
      margin: "0 0 4px",
      fontSize: 20,
      fontWeight: 600
    }}>
        Delivery payment plans
      </h2>
      <p style={{
      margin: "0 0 18px",
      color: C.muted,
      fontSize: 14
    }}>
        Patients on a delivery payment plan, by branch. “Still delivering” means a
        prescription in the last {windowDays} days.
      </p>

      {loading && <div style={{
      color: C.muted
    }}>Loading…</div>}
      {error && <div style={{
      background: C.badBg,
      color: C.bad,
      padding: 12,
      borderRadius: 8
    }}>
          Could not load payments: {error}
        </div>}

      {!loading && !error && branches.length === 0 && <div style={{
      color: C.muted
    }}>No branches in scope.</div>}

      {!loading && !error && branches.length > 0 && <>
          <SummaryStrip totals={totals} />
          <div style={{
        border: `1px solid ${C.line}`,
        borderRadius: 10,
        overflow: "hidden"
      }}>
            <div style={{
          ...rowStyle,
          background: C.head,
          fontWeight: 600,
          cursor: "default"
        }}>
              <div>Branch</div>
              <div style={num}>Ever on a plan</div>
              <div style={num}>Covered</div>
              <div style={num}>Expired</div>
              <div style={num}>Expired &amp; still delivering</div>
            </div>

            {branches.map(b => {
          const open = openId === b.pharmacyId;
          const alarm = (b.expiredActive || 0) > 0;
          return <div key={b.pharmacyId} style={{
            borderTop: `1px solid ${C.line}`
          }}>
                  <div style={{
              ...rowStyle,
              background: open ? "#fafaf7" : "#fff"
            }} onClick={() => toggle(b)} role="button" tabIndex={0} onKeyDown={e => (e.key === "Enter" || e.key === " ") && toggle(b)}>
                    <div style={{
                fontWeight: 500
              }}>
                      <span style={{
                  color: C.muted,
                  marginRight: 8
                }}>{open ? "▾" : "▸"}</span>
                      {b.pharmacyName}
                      {b.error && <span style={{
                  color: C.bad,
                  fontSize: 12,
                  marginLeft: 8
                }}>
                          (failed to load)
                        </span>}
                    </div>
                    <div style={num}>{b.ever || 0}</div>
                    <div style={{
                ...num,
                color: C.good
              }}>{b.covered || 0}</div>
                    <div style={num}>{b.expired || 0}</div>
                    <div style={num}>
                      <Pill value={b.expiredActive || 0}
                // Green only when we KNOW nobody expired is still being
                // delivered to. If the activity lookup failed, say so
                // rather than showing a reassuring zero.
                unknown={b.activityKnown === false} alarm={alarm} />
                    </div>
                  </div>

                  {open && <BranchDetail branch={b} data={detail[b.pharmacyId]} busy={detailBusy === b.pharmacyId} filter={filter} setFilter={setFilter} onRemind={p => compose(b, p)} />}
                </div>;
        })}
          </div>
        </>}

      {toast && <div style={{
      marginTop: 14,
      padding: 12,
      borderRadius: 8,
      background: C.warnBg,
      color: C.warn,
      fontSize: 14
    }}>
          {toast}
        </div>}

      {preview && <PreviewDialog preview={preview} sending={sending} onCancel={() => setPreview(null)} onSend={send} />}
    </div>;
}

/* -------------------------------------------------------------------------- */

const rowStyle = {
  display: "grid",
  gridTemplateColumns: "2fr 1fr 1fr 1fr 1.4fr",
  gap: 8,
  alignItems: "center",
  padding: "12px 14px",
  fontSize: 14,
  cursor: "pointer"
};
const num = {
  textAlign: "right",
  fontVariantNumeric: "tabular-nums"
};
function Pill({
  value,
  alarm,
  unknown
}) {
  if (unknown) {
    return <span title="Could not determine which patients are still receiving deliveries" style={{
      ...pillBase,
      background: C.warnBg,
      color: C.warn
    }}>
        unknown
      </span>;
  }
  return <span style={{
    ...pillBase,
    background: alarm ? C.badBg : C.goodBg,
    color: alarm ? C.bad : C.good,
    fontWeight: alarm ? 700 : 500
  }}>
      {value}
    </span>;
}
const pillBase = {
  display: "inline-block",
  minWidth: 34,
  padding: "3px 10px",
  borderRadius: 999,
  fontSize: 13,
  textAlign: "center"
};
function SummaryStrip({
  totals
}) {
  const cell = (label, value, color) => <div style={{
    flex: 1,
    background: "#fff",
    border: `1px solid ${C.line}`,
    borderRadius: 10,
    padding: "12px 14px"
  }}>
      <div style={{
      fontSize: 12,
      color: C.muted
    }}>{label}</div>
      <div style={{
      fontSize: 22,
      fontWeight: 600,
      color: color || C.ink
    }}>{value}</div>
    </div>;
  return <div style={{
    display: "flex",
    gap: 12,
    marginBottom: 16
  }}>
      {cell("Patients ever on a plan", totals.ever)}
      {cell("Currently covered", totals.covered, C.good)}
      {cell("Expired", totals.expired)}
      {cell("Expired & still delivering", totals.expiredActive, totals.expiredActive > 0 ? C.bad : C.good)}
    </div>;
}
function BranchDetail({
  branch,
  data,
  busy,
  filter,
  setFilter,
  onRemind
}) {
  if (busy) return <div style={{
    padding: 14,
    color: C.muted
  }}>Loading patients…</div>;
  if (!data) return null;
  if (data.error) {
    return <div style={{
      padding: 14,
      color: C.bad
    }}>Could not load: {data.error}</div>;
  }
  const all = data.patients || [];
  const shown = filter === "expired" ? all.filter(p => p.state === "expired") : filter === "active" ? all.filter(p => p.state === "expired" && p.stillDelivering) : all;
  const tab = (key, label) => <button key={key} onClick={e => {
    e.stopPropagation();
    setFilter(key);
  }} style={{
    border: `1px solid ${filter === key ? C.ink : C.line}`,
    background: filter === key ? C.ink : "#fff",
    color: filter === key ? "#fff" : C.ink,
    borderRadius: 999,
    padding: "4px 12px",
    fontSize: 13,
    cursor: "pointer"
  }}>
      {label}
    </button>;
  return <div style={{
    padding: "6px 14px 16px",
    background: "#fafaf7"
  }} onClick={e => e.stopPropagation()}>
      <div style={{
      display: "flex",
      gap: 8,
      margin: "6px 0 12px"
    }}>
        {tab("all", `All (${all.length})`)}
        {tab("expired", `Expired (${data.expired || 0})`)}
        {tab("active", `Expired & still delivering (${data.expiredActive || 0})`)}
      </div>

      {shown.length === 0 && <div style={{
      color: C.muted,
      fontSize: 14
    }}>Nothing to show here.</div>}

      {shown.map(p => <div key={p.patientId} style={{
      display: "grid",
      gridTemplateColumns: "1.6fr 1fr 1fr 1fr auto",
      gap: 10,
      alignItems: "center",
      padding: "10px 12px",
      marginBottom: 6,
      background: "#fff",
      border: `1px solid ${C.line}`,
      borderRadius: 8,
      fontSize: 13
    }}>
          <div>
            <div style={{
          fontWeight: 500
        }}>{p.name}</div>
            <div style={{
          color: C.muted,
          fontSize: 12
        }}>
              {p.email || "no email on record"}
              {p.stillDelivering && <span style={{
            color: C.bad,
            fontWeight: 600,
            marginLeft: 8
          }}>
                  still delivering
                </span>}
            </div>
          </div>
          <div><StateTag state={p.state} /></div>
          <div style={{
        color: C.muted
      }}>
            {p.lastPlan || "—"}{p.lastAmount != null ? ` · ${fmtMoney(p.lastAmount)}` : ""}
          </div>
          <div style={{
        color: C.muted
      }}>
            {p.state === "unknown" ? "no end date" : `until ${fmtDate(p.latestExpiry)}`}
          </div>
          <div>
            {p.state !== "covered" && <button onClick={() => onRemind(p)} disabled={!p.email} title={p.email ? "Compose a renewal reminder" : "No email address on record"} style={{
          border: `1px solid ${p.email ? C.ink : C.line}`,
          background: p.email ? "#fff" : "#f4f4f1",
          color: p.email ? C.ink : C.muted,
          borderRadius: 8,
          padding: "6px 12px",
          fontSize: 13,
          cursor: p.email ? "pointer" : "not-allowed",
          whiteSpace: "nowrap"
        }}>
                Email reminder
              </button>}
          </div>
        </div>)}
    </div>;
}
function StateTag({
  state
}) {
  const map = {
    covered: ["Covered", C.good, C.goodBg],
    expired: ["Expired", C.bad, C.badBg],
    unknown: ["No end date", C.warn, C.warnBg]
  };
  const [label, fg, bg] = map[state] || ["—", C.muted, C.head];
  return <span style={{
    ...pillBase,
    background: bg,
    color: fg,
    minWidth: 0,
    padding: "3px 10px"
  }}>
      {label}
    </span>;
}

// The composed email is shown before anything leaves the building. These go to
// patients, so a preview step is worth the extra click.
function PreviewDialog({
  preview,
  sending,
  onCancel,
  onSend
}) {
  return <div style={{
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.35)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000
  }} onClick={onCancel}>
      <div style={{
      background: "#fff",
      borderRadius: 12,
      padding: 20,
      width: "min(620px, 92vw)",
      maxHeight: "85vh",
      overflow: "auto"
    }} onClick={e => e.stopPropagation()}>
        <h3 style={{
        margin: "0 0 4px",
        fontSize: 17
      }}>Send renewal reminder</h3>
        <div style={{
        color: C.muted,
        fontSize: 13,
        marginBottom: 4
      }}>
          To: <strong>{preview.to}</strong> ({preview.patient?.name})
        </div>
        <div style={{
        color: C.muted,
        fontSize: 13,
        marginBottom: 12
      }}>
          Subject: {preview.subject}
        </div>
        <div style={{
        border: `1px solid ${C.line}`,
        borderRadius: 8,
        padding: 14,
        fontSize: 14,
        lineHeight: 1.5,
        background: "#fcfcfa"
      }} dangerouslySetInnerHTML={{
        __html: preview.html
      }} />
        <div style={{
        display: "flex",
        justifyContent: "flex-end",
        gap: 8,
        marginTop: 16
      }}>
          <button onClick={onCancel} disabled={sending} style={{
          border: `1px solid ${C.line}`,
          background: "#fff",
          color: C.ink,
          borderRadius: 8,
          padding: "8px 16px",
          cursor: "pointer"
        }}>
            Cancel
          </button>
          <button onClick={onSend} disabled={sending} style={{
          border: "none",
          background: C.ink,
          color: "#fff",
          borderRadius: 8,
          padding: "8px 16px",
          cursor: sending ? "default" : "pointer",
          opacity: sending ? 0.6 : 1
        }}>
            {sending ? "Sending…" : "Send email"}
          </button>
        </div>
      </div>
    </div>;
}
